import { useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import {
  getPlatformSettings,
  fetchPlatformSettings,
  getFairSeedConfig,
} from "../lib/platformSettings";

import { getInvestmentInterpretation }from "../utils/riskInvestmentInterpretation";
import axios from "axios";
import { FAIR_INVESTMENT_CONFIG } from "../config/fairSettings";
import { ensureCsrfToken } from "../lib/csrf";
import { Navigate } from "react-router-dom";
import { usePortfolioOptimizationEvidence } from "../modules/portfolio_decision/portfolio_analysis/context/PortfolioOptimizationEvidenceContext";

import ExecutiveCard from "../components/executive/ExecutiveCard";
import ExecutiveSection from "../components/executive/ExecutiveSection";

import { formatMoney, formatPercent } from "../utils/formatters";
import LoadingOrbit from "../components/LoadingOrbit";
import EconomicBasisDisclosure from "../components/EconomicBasisDisclosure";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

export default function FairPortfolio({ embedded = false }) {
  const { acceptOptimization, canProduce, acceptanceError } = usePortfolioOptimizationEvidence();
  const [continueToAnalysis, setContinueToAnalysis] = useState(false);
  const [investment, setInvestment] = useState(0);
  const [selectedInvestment, setSelectedInvestment] = useState(0);
  const [executionInvestment, setExecutionInvestment] = useState(0);
  
  const [simulation, setSimulation] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [optimizationError, setOptimizationError] = useState(null);
  const [publishMessage, setPublishMessage] = useState(null);
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [viewMode, setViewMode] = useState("analysis");
  const [showArchived, setShowArchived] = useState(false);

  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [governanceComment, setGovernanceComment] = useState("");
  const [snapshotViewMode, setSnapshotViewMode] = useState("executive");
  const [showCorrelationDetails, setShowCorrelationDetails] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const [currentUser, setCurrentUser] = useState(null);
  
  const [presentationMode, setPresentationMode] = useState(false);
  const [correlation, setCorrelation] = useState(null);

  const [settings, setSettings] = useState(getPlatformSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const simulationRequestId = useRef(0);
  const optimizationRequestId = useRef(0);

  useEffect(() => () => {
    simulationRequestId.current += 1;
    optimizationRequestId.current += 1;
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const freshSettings = await fetchPlatformSettings();

        if (!isActive) return;

        setSettings(freshSettings);
      } catch (err) {
        console.error("Error loading platform settings:", err);
      } finally {
        if (isActive) {
          setSettingsLoaded(true);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);


  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return null;
  }


 const fairParams = useMemo(() => ({
    simulations: Number(settings.simulations || 5000),
    seed: getFairSeedConfig(settings).seed,
  }), [
    settings.simulations,
    settings.seedMode,
    settings.seedValue,
  ]);

  useEffect(() => {
    if (!settingsLoaded) return;

    axios
      .get("/api/digital-risk/dashboard/portfolio-correlation/")
      .then((res) => setCorrelation(res.data))
      .catch(console.error);
  }, [settingsLoaded, refreshKey]);

  useEffect(() => {
    axios
      .get("/api/check_session/", {
        withCredentials: true,
      })
      .then((res) => {
        setCurrentUser(res.data.username || "System");
      })
      .catch(() => setCurrentUser("System"));

  }, []);

  useEffect(() => {
    const handleScenarioChange = () => {
      optimizationRequestId.current += 1;
      setOptimization(null);
      setOptimizationError(null);
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener("fair-scenarios-changed", handleScenarioChange);

    return () => {
      window.removeEventListener("fair-scenarios-changed", handleScenarioChange);
    };
  }, []);

 
  useEffect(() => {
    const requestId = ++simulationRequestId.current;

    const fetchSimulation = async () => {
      if (!settingsLoaded) return;

      setLoading(true);

      try {
        const res = await axios.get(
          "/api/digital-risk/dashboard/risk-portfolio-analysis/",
          {
            params: {
              investment: executionInvestment,
              ...fairParams,
              _refresh: refreshKey,
            },
          }
        );

        if (requestId !== simulationRequestId.current) return;

        setSimulation(res.data);
        console.log(
          "Fair Portfolio Impact",
          res.data?.impact?.eal_reduction
        );

      } catch (error) {
        if (requestId !== simulationRequestId.current) return;

        console.error("Error loading risk portfolio analysis:", error);
      } finally {
        if (requestId === simulationRequestId.current) {
          setLoading(false);
        }
      }
    };

    fetchSimulation();

    return () => {
      // Request identity, rather than completion timing, owns result acceptance.
    };
  }, [
    settingsLoaded,
    executionInvestment,
    fairParams.simulations,
    fairParams.seed,
    refreshKey,
  ]);

  useEffect(() => {
    if (!settingsLoaded) return;
    const requestId = ++optimizationRequestId.current;
    const capitalSnapshot = executionInvestment;

    setLoading(true);
    setOptimizationError(null);
    const fetchOptimization = async () => {
      try {
        const csrfToken = await ensureCsrfToken();
        const res = await axios.post(
          "/api/digital-risk/dashboard/portfolio-capital-optimization/",
          {
            available_capital: capitalSnapshot,
            capture_analytical_evidence: true,
            simulations: fairParams.simulations,
            seed: fairParams.seed,
          },
          {
            withCredentials: true,
            headers: { "X-CSRFToken": csrfToken },
          }
        );
        if (requestId !== optimizationRequestId.current) return;
        setOptimization(res.data);
        setInvestment(capitalSnapshot);
      } catch (error) {
        if (requestId !== optimizationRequestId.current) return;
        setOptimizationError(error.response?.data?.detail || "Unable to run Portfolio capital optimization.");
      } finally {
        if (requestId === optimizationRequestId.current) setLoading(false);
      }
    };

    fetchOptimization();
  }, [settingsLoaded, executionInvestment, fairParams.simulations, fairParams.seed, refreshKey]);


  useEffect(() => {
    if (embedded) return;

    axios
      .get(
        "/api/digital-risk/dashboard/portfolio-snapshot-history/?scope=all"
      )
      .then((res) => setSnapshotHistory(res.data))
      .catch(console.error);
  }, [embedded]);


  const reviewSnapshot = (snapshot) => {
      setSelectedSnapshot(snapshot);
      setGovernanceComment(snapshot.comments || "");
      setShowSnapshotModal(true);
    };

  const publishSnapshot = async () => {
    try {
      setPublishMessage(null);

      const response = await axios.post(
        "/api/digital-risk/dashboard/publish-portfolio-snapshot/",
        {
          investment,
          snapshot_name: `Governance Snapshot - ${new Date().toLocaleDateString()}`,
        },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": getCookie("csrftoken"),
          },
          params: {
            simulations: fairParams.simulations,
            seed: fairParams.seed,
          },
        }
      );

      setPublishMessage({
        type: "success",
        text: "Governance snapshot published successfully.",
      });

      setTimeout(() => {
        setPublishMessage(null);
      }, 5000);

      axios
        .get("/api/digital-risk/dashboard/portfolio-snapshot-history/?scope=all")
        .then((res) => setSnapshotHistory(res.data));
    } catch (error) {
      setPublishMessage({
        type: "error",
        text:
          error.response?.data?.detail ||
          "Unable to publish governance snapshot.",
      });

      setTimeout(() => {
        setPublishMessage(null);
      }, 5000);
    }
  };

  const approveSnapshot = async (snapshotId, comments = "") => {
    try {
     await axios.post(
        `/api/digital-risk/dashboard/approve-portfolio-snapshot/${snapshotId}/`,
        { comments, },
        { withCredentials: true }
      );

      showPublishMessage(
        "success",
        "Governance snapshot approved successfully."
      );

      const historyResponse = await axios.get(
        "/api/digital-risk/dashboard/portfolio-snapshot-history/?scope=all"
      );

      setSnapshotHistory(historyResponse.data);

    } catch (error) {
      showPublishMessage(
        "error",
        error.response?.data?.detail ||
          "Unable to approve governance snapshot."
      );

      console.error(error);
    }
  };


  const archiveSnapshot = async (snapshotId) => {
    try {

      await axios.post(
        `/api/digital-risk/dashboard/archive-portfolio-snapshot/${snapshotId}/`,
          {},
          { withCredentials: true }
      );

      showPublishMessage(
        "success",
        "Governance snapshot archived successfully."
      );

      const historyResponse = await axios.get(
        "/api/digital-risk/dashboard/portfolio-snapshot-history/?scope=all"
      );

      setSnapshotHistory(historyResponse.data);

    } catch (error) {

      showPublishMessage(
        "error",
        error.response?.data?.detail ||
        "Unable to archive governance snapshot."
      );

      console.error(error);
    }
  };



  const showPublishMessage = (type, text) => {
    setPublishMessage({ type, text });

    setTimeout(() => {
      setPublishMessage(null);
    }, 5000);
  };


//  if (!simulation) return <div>Loading...</div>;

  if (!simulation) {
    return (
      <LoadingOrbit
        message="Loading Scenario Data..."
        submessage="Preparing data analytics"
      />
    );
  }



  if (simulation.empty) {
    return (
      <div className="p-6 space-y-6">

        {!embedded && (

         <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Investment Optimization
            </h1>

            <p className="text-sm text-slate-500 mt-1">
               Optimize cybersecurity investment decisions based on quantified risk reduction and financial impact analysis.
            </p>

            <p className="text-xs text-slate-400 mt-1">
              Monte Carlo: {settings.simulations.toLocaleString()} simulations ·
              Confidence: {settings.confidenceLevel} ·
              Loss model: {settings.lossModel}
            </p>
          </div>
        )}

        <div className="bg-white border rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-semibold text-gray-900">
            No Active Risk Scenarios
          </h2>

          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
            The investment optimization engine requires active FAIR risk scenarios
            to generate portfolio analysis, concentration metrics and investment simulations.
          </p>

          <div className="mt-6 text-sm text-gray-400">
            Create and activate risk scenarios to begin analysis.
          </div>
        </div>
      </div>
    );
  }


  const contributors =
    (
      simulation?.allocation?.map((item) => ({
        scenario_id: item.scenario_id,
        incident_type: item.incident_type,
        asset_name: item.asset_name,
        expected_annual_loss: item.baseline_eal,
        eal_contribution_pct: item.weight_pct,
      })) || []
    ).sort(
      (a, b) =>
        (b.eal_contribution_pct || 0) -
        (a.eal_contribution_pct || 0)
    );

  const top1 = contributors[0]?.eal_contribution_pct || 0;

 {/* const top3 = contributors
    .slice(0, 3)
    .reduce((sum, item) => sum + (item.eal_contribution_pct || 0), 0);  */}

  const totalContribution = contributors
    .reduce((sum, item) => sum + (item.eal_contribution_pct || 0), 0);


  const concentrationLevel =
    top1 >= 60
      ? "High concentration"
      : top1 >= 35
      ? "Moderate concentration"
      : "Distributed risk";

  const baselineEAL =
    simulation?.baseline?.expected_annual_loss ?? 0;

  const baselineP90 =
    simulation?.baseline?.p90 ?? 0;

  const baselineP95 =
   simulation?.baseline?.p95 ?? 0;

  const ealReduction = optimization?.impact?.eal_reduction ?? 0;

  const p90Reduction = optimization?.impact?.p90_reduction ?? 0;

  const p95Reduction = optimization?.impact?.p95_reduction ?? 0;

  const roi = optimization?.impact?.roi ?? 0;
  const roiInterpretation =  getInvestmentInterpretation(roi);
  const netBenefit = optimization?.impact?.net_benefit ?? 0;

  const formatROI = (value) => {
    if (value === null || value === undefined) return "—";
    return `${Number(value).toFixed(1)}x`;
  };


  //const baselineCurve = simulation?.curve ?? [];
  //const adjustedCurve = simulation?.control_curve ?? [];

  const visibleSnapshots = showArchived
    ? snapshotHistory
    : snapshotHistory.filter((snapshot) => snapshot.status !== "archived");


  const getPreviousApprovedSnapshot = (snapshot) => {
    if (!snapshot) return null;

    const approvedSnapshots = snapshotHistory
      .filter((item) => item.status === "approved")
      .sort(
        (a, b) =>
          new Date(b.snapshot_date) - new Date(a.snapshot_date)
      );

    return approvedSnapshots.find(
      (item) =>
        item.id !== snapshot.id &&
        new Date(item.snapshot_date) < new Date(snapshot.snapshot_date)
    );
  };

  const previousApprovedSnapshot = selectedSnapshot
    ? getPreviousApprovedSnapshot(selectedSnapshot)
    : null;


  const topShare = Number(contributors?.[0]?.eal_contribution_pct || 0);

  const concentrationClassification =
    topShare >= 50
      ? "Critical Concentration"
      : topShare >= 35
      ? "Highly Concentrated"
      : topShare >= 20
      ? "Moderately Concentrated"
      : "Well Diversified";


  const investmentStep = Number(settings?.investmentStep || 50000);

  const investmentCeilingRatio = Number(settings?.investmentCeilingRatio || 0.75);

  const portfolioEalForCeiling = Number(
    simulation?.baseline?.expected_annual_loss ??
    simulation?.expected_annual_loss ??
    0
  );

  const dynamicInvestmentMax = Math.max(
    FAIR_INVESTMENT_CONFIG.max,
    Math.ceil(
      (portfolioEalForCeiling * investmentCeilingRatio) / investmentStep
    ) * investmentStep
  );

  const normalizeInvestment = (value) => {
    return Math.round(Number(value || 0) / investmentStep) * investmentStep;
  };

  return (
    <div className="p-6 space-y-6">
      {continueToAnalysis && <Navigate to="/portfolio-analysis" />}
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold">
            Portfolio Investment Optimization
          </h1>

          <p className="text-gray-500 mt-1">
            Optimize cybersecurity investment decisions based on quantified portfolio exposure and financial impact analysis.
          </p>
        </div>
      )}
      {!embedded && <div className="flex gap-2 mt-2 mb-4">
        <button
          onClick={() => setViewMode("analysis")}
          className={`px-4 py-2 rounded-xl text-sm border transition ${
            viewMode === "analysis"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Portfolio Analysis
        </button>

        <button
          onClick={() => setViewMode("history")}
          className={`px-4 py-2 rounded-xl text-sm border transition ${
            viewMode === "history"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Snapshot History
        </button>
      </div>}

      {!embedded && <div className="flex justify-end">
        <button
          onClick={publishSnapshot}
          className="border border-gray-200 rounded-lg px-3 py-1 text-sm bg-gray-50 hover:bg-gray-100"
        >
          Publish Governance Snapshot
        </button>

        <button
          onClick={() => {
            const nextMode = !presentationMode;
            setPresentationMode(nextMode);

            if (nextMode && !document.fullscreenElement) {
              document.documentElement.requestFullscreen();
            }

            if (!nextMode && document.fullscreenElement) {
              document.exitFullscreen();
            }
          }}
          className={`
            px-4 py-2 rounded-xl text-sm border transition
            ${
              presentationMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }
          `}
        >
          🖥️ {presentationMode ? "Exit Presentation" : "Presentation Mode"}
        </button>
        
      </div>}
            {publishMessage && (
        <div
          className={`mt-3 rounded-xl border px-4 py-3 text-sm ${
            publishMessage.type === "success"
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {publishMessage.text}
        </div>
      )}


      {viewMode === "history" && (
        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5">

          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">
                Governance Snapshot History
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Official portfolio governance evidence snapshots.
              </p>
            </div>

            <label className="flex items-center mr-4 gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">

              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="text-left py-3">Date</th>
                  <th className="text-left py-3">Snapshot</th>
                  <th className="text-left py-3">Investment</th>
                  <th className="text-left py-3">EAL</th>
                  <th className="text-left py-3">P95</th>
                  <th className="text-left py-3">Appetite</th>
                  <th className="text-left py-3">Status</th>
                  <th className="text-left py-3">Actions</th>
                </tr>
              </thead>

              <tbody>
                {visibleSnapshots.map((snapshot) => (
                  <tr
                    key={snapshot.id}
                    className="border-b border-gray-100"
                  >
                    <td className="py-3">
                      {new Date(
                        snapshot.snapshot_date
                      ).toLocaleDateString()}
                    </td>

                    <td className="py-3">
                      {snapshot.snapshot_name}
                    </td>

                    <td className="py-3">
                      {formatMoney(snapshot.investment, settings.currency)}
                    </td>

                    <td className="py-3">
                      {formatMoney(snapshot.expected_annual_loss, settings.currency)}
                    </td>

                    <td className="py-3">
                      {formatMoney(snapshot.p95, settings.currency)}
                    </td>

                    <td className="py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          snapshot.appetite_status === "critical"
                            ? "bg-red-100 text-red-700"
                            : snapshot.appetite_status === "warning"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {snapshot.appetite_status}
                      </span>
                    </td>
                    
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        snapshot.status === "approved"
                          ? "bg-green-100 text-green-700"
                          : snapshot.status === "draft"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-700"
                      }`}>
                        {snapshot.status}
                      </span>
                    </td>
                 
                    <td className="py-3">

                      <button
                        onClick={() => {
                          setSelectedSnapshot(snapshot);
                          setSnapshotViewMode("executive");
                          setShowSnapshotModal(true);
                        }}
                        className="px-3 py-1 rounded-lg border border-slate-300 text-slate-700 text-xs hover:bg-slate-100"
                      >
                        Executive View
                      </button>

                      <button
                        onClick={() => {
                          setSelectedSnapshot(snapshot);
                          setSnapshotViewMode("governance");
                          setShowSnapshotModal(true);
                        }}
                        className="px-3 py-1 rounded-lg border border-slate-300 text-slate-700 text-xs hover:bg-slate-100"
                      >
                        Governance Review
                      </button>

                      {snapshot.status !== "archived" && (
                        <button
                          onClick={() => archiveSnapshot(snapshot.id)}
                          className="px-3 py-1 rounded-lg ml-2 border border-slate-300 text-slate-600 text-xs hover:bg-slate-100"
                        >
                          Archive
                        </button>
                      )}

                    </td>

                  </tr>
                ))}
              </tbody>
            </table>

            {showSnapshotModal && selectedSnapshot && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

                <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-6xl max-h-[90vh] flex flex-col">
                  <div className="p-6 overflow-y-auto flex-1">

                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-semibold">
                        Governance Snapshot Review
                      </h2>

                      <button
                        onClick={() => setShowSnapshotModal(false)}
                        className="text-gray-500 hover:text-black"
                      >
                        ✕
                      </button>
                    </div>


                    <div className="grid grid-cols-2 gap-4">

                      <div className="border rounded-xl p-4">
                        <div className="text-xs text-gray-500">Snapshot</div>
                        <div className="font-semibold">
                          {selectedSnapshot.snapshot_name}
                        </div>
                      </div>

                      <div className="border rounded-xl p-4">
                        <div className="text-xs text-gray-500">Investment</div>
                        <div className="font-semibold">
                          {formatMoney(selectedSnapshot.investment, settings.currency)}
                        </div>
                      </div>

                      <div className="border rounded-xl p-4">
                        <div className="text-xs text-gray-500">EAL</div>
                        <div className="font-semibold">
                          {formatMoney(selectedSnapshot.expected_annual_loss, settings.currency)}
                        </div>
                      </div>

                      <div className="border rounded-xl p-4">
                        <div className="text-xs text-gray-500">P95</div>
                        <div className="font-semibold">
                          {formatMoney(selectedSnapshot.p95, settings.currency)}
                        </div>
                      </div>

                      <div className="border rounded-xl p-4">
                        <div className="text-xs text-gray-500">Risk Appetite</div>
                        <div className="font-semibold capitalize">
                          {selectedSnapshot.appetite_status}
                        </div>
                      </div>

                      <div className="border rounded-xl p-4">
                        <div className="text-xs text-gray-500">Lifecycle Status</div>
                        <div className="font-semibold capitalize">
                          {selectedSnapshot.status}
                        </div>
                      </div>

                    </div>


                    <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">

                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs text-slate-500">
                          Scope
                        </div>

                        <div className="font-semibold capitalize">
                          {selectedSnapshot.metadata?.scope}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs text-slate-500">
                          Simulation Mode
                        </div>

                        <div className="font-semibold">
                          {selectedSnapshot.metadata?.simulation_mode}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs text-slate-500">
                          Simulations
                        </div>

                        <div className="font-semibold">
                          {selectedSnapshot.metadata?.simulations?.toLocaleString()}
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="text-xs text-slate-500">
                          Seed
                        </div>

                        <div className="font-semibold">
                          {selectedSnapshot.metadata?.seed ?? "Randomized"}
                        </div>
                      </div>
                       
                      {snapshotViewMode === "governance" && (

                        <div className="mt-6">
                          <h3 className="text-lg font-semibold mb-3">
                            Governance Lifecycle
                          </h3>

                          <div className="flex items-center gap-4">

                            <div
                              className={`flex-1 rounded-xl border p-4 ${
                                selectedSnapshot.status === "draft"
                                  ? "bg-amber-50 border-amber-200"
                                  : "bg-slate-50 border-slate-200"
                              }`}
                            >
                              <div className="text-xs text-slate-500">
                                Draft
                              </div>

                              <div className="font-semibold mt-1">
                                Snapshot Published
                              </div>
                            </div>

                            <div className="h-[2px] flex-1 bg-slate-200" />

                            <div
                              className={`flex-1 rounded-xl border p-4 ${
                                selectedSnapshot.status === "approved"
                                  ? "bg-green-50 border-green-200"
                                  : "bg-slate-50 border-slate-200"
                              }`}
                            >
                              <div className="text-xs text-slate-500">
                                Approved
                              </div>

                              <div className="font-semibold mt-1">
                                Governance Review
                              </div>
                            </div>

                            <div className="h-[2px] flex-1 bg-slate-200" />

                            <div
                              className={`flex-1 rounded-xl border p-4 ${
                                selectedSnapshot.status === "archived"
                                  ? "bg-blue-50 border-blue-200"
                                  : "bg-slate-50 border-slate-200"
                              }`}
                            >
                              <div className="text-xs text-slate-500">
                                Archived
                              </div>

                              <div className="font-semibold mt-1">
                                Historical Evidence
                              </div>
                            </div>
                          </div>
                        </div>
                      )} 

                    </div>

                    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <h3 className="text-lg font-semibold">
                            Governance Assessment
                          </h3>

                          <p className="text-sm text-slate-600 mt-2 leading-relaxed">

                            Current portfolio exposure remains{" "}

                            <strong>
                              {selectedSnapshot.appetite_status}
                            </strong>

                            {" "}against defined appetite thresholds.

                            The simulated investment of{" "}

                            <strong>
                              {formatMoney(
                                selectedSnapshot.investment,
                                settings.currency
                              )}
                            </strong>

                            {" "}generated an estimated ROI of{" "}

                            <strong>
                              {(selectedSnapshot.roi || 0).toFixed(2)}x
                            </strong>

                            {" "}while reducing expected annual loss exposure.

                          </p>
                        </div>

                        <div className="text-right">

                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Appetite Status
                          </div>

                          <div className="text-2xl font-bold capitalize mt-1">
                            {selectedSnapshot.appetite_status}
                          </div>

                        </div>

                      </div>

                    </div>


                    {selectedSnapshot.metadata?.recommendations?.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-3">
                          Board Recommendations
                        </h3>

                        <div className="space-y-3">
                          {selectedSnapshot.metadata.recommendations.map((item, index) => (
                            <div
                              key={index}
                              className="rounded-xl border border-gray-200 bg-slate-50 p-4"
                            >
                              <div className="font-semibold text-sm">
                                {item.title}
                              </div>

                              <div className="text-sm text-gray-600 mt-1">
                                {item.recommendation}
                              </div>

                              <div className="text-xs text-gray-500 mt-2">
                                {item.rationale}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}                  


                    {selectedSnapshot.metadata?.stress_tests?.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-3">
                          Stress Tests
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedSnapshot.metadata.stress_tests.map((item, index) => (
                            <div
                              key={index}
                              className="rounded-xl border border-red-100 bg-red-50 p-4"
                            >
                              <div className="font-semibold text-sm">
                                {item.scenario}
                              </div>

                              <div className="text-sm text-gray-600 mt-1">
                                {item.description}
                              </div>

                              <div className="text-xl font-semibold text-red-700 mt-3">
                                +{Number(item.delta_pct || 0).toFixed(0)}%
                              </div>

                              <div className="text-xs text-gray-500">
                                Stress impact
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {previousApprovedSnapshot && (
                      <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4">
                        <h3 className="text-lg font-semibold text-blue-950 mb-3">
                          Comparison Against Previous Approved Snapshot
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-xs text-blue-700 uppercase tracking-wide">
                              EAL Delta
                            </div>
                            <div className="text-lg font-semibold text-blue-950">
                              {formatMoney(
                                selectedSnapshot.expected_annual_loss -
                                  previousApprovedSnapshot.expected_annual_loss, settings.currency
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-blue-700 uppercase tracking-wide">
                              P95 Delta
                            </div>
                            <div className="text-lg font-semibold text-blue-950">
                              {formatMoney(
                                selectedSnapshot.p95 - previousApprovedSnapshot.p95, settings.currency
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-blue-700 uppercase tracking-wide">
                              Concentration Delta
                            </div>
                            <div className="text-lg font-semibold text-blue-950">
                              {(
                                selectedSnapshot.top_concentration_pct -
                                previousApprovedSnapshot.top_concentration_pct
                              ).toFixed(1)}{" "}
                              pts
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-blue-700 uppercase tracking-wide">
                              ROI Delta
                            </div>
                            <div className="text-lg font-semibold text-blue-950">
                              {(
                                (selectedSnapshot.roi || 0) -
                                (previousApprovedSnapshot.roi || 0)
                              ).toFixed(1)}
                              x
                            </div>
                          </div>
                        </div>
                      </div>
                    )}


                    {selectedSnapshot.metadata?.risk_evolution && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-3">
                          Risk Evolution
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="text-xs text-slate-500">
                              Direction
                            </div>

                            <div className="text-xl font-semibold capitalize mt-1">
                              {
                                selectedSnapshot.metadata.risk_evolution
                                  ?.trend?.direction
                              }
                            </div>
                          </div>

                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="text-xs text-slate-500">
                              EAL Trend
                            </div>

                            <div className="text-xl font-semibold mt-1">
                              {
                                selectedSnapshot.metadata.risk_evolution
                                  ?.trend?.eal_pct
                              }%
                            </div>
                          </div>

                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="text-xs text-slate-500">
                              P95 Trend
                            </div>

                            <div className="text-xl font-semibold mt-1">
                              {
                                selectedSnapshot.metadata.risk_evolution
                                  ?.trend?.p95_pct
                              }%
                            </div>
                          </div>

                        </div>
                      </div>
                    )}

                    {selectedSnapshot.metadata?.allocation?.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-3">
                          Investment Allocation
                        </h3>

                        <div className="space-y-3">
                          {selectedSnapshot.metadata.allocation.map((item) => (
                            <div
                              key={item.scenario_id}
                              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold">
                                    {item.incident_type}
                                  </div>

                                  <div className="text-sm text-slate-500 mt-1">
                                    {item.asset_name} · {Number(item.weight_pct || 0).toFixed(1)}% of portfolio risk
                                  </div>
                                </div>

                                <div className="text-right">
                                  <div className="text-xs text-slate-500">
                                    Allocated Investment
                                  </div>

                                  <div className="text-lg font-semibold">
                                    {formatMoney(item.allocated_investment, settings.currency)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
  
                    {snapshotViewMode === "governance" && (

              //      {selectedSnapshot.metadata?.curves?.baseline && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-3">
                          Frozen Loss Exceedance Curves
                        </h3>

                        <div className="h-[320px] rounded-2xl border border-slate-200 bg-white p-4">
                          <ResponsiveContainer width="100%" height="100%">

                            <LineChart
                              data={
                                selectedSnapshot.metadata.curves.baseline
                              }
                            >

                              <CartesianGrid strokeDasharray="3 3" />

                              <XAxis
                                dataKey="loss"
                                type="number"
                                domain={[0, "dataMax"]}
                                tickFormatter={(v) =>
                                  formatMoney(v, settings.currency)
                                }
                              />

                              <YAxis
                                tickFormatter={(v) =>
                                  `${(v * 100).toFixed(0)}%`
                                }
                              />

                              <Tooltip
                                formatter={(v) => [
                                  `${(v * 100).toFixed(2)}%`,
                                  "Exceedance Probability",
                                ]}

                                labelFormatter={(v) =>
                                  `Loss: ${formatMoney(v, settings.currency)}`
                                }
                              />

                              <Legend />

                              <Line
                                type="monotone"
                                dataKey="exceedance_probability"
                                stroke="#64748B"
                                strokeWidth={2}
                                dot={false}
                                name="Baseline"
                              />

                              {selectedSnapshot.metadata?.curves?.adjusted && (
                                <Line
                                  data={
                                    selectedSnapshot.metadata.curves.adjusted
                                  }
                                  type="monotone"
                                  dataKey="exceedance_probability"
                                  stroke="#2563EB"
                                  strokeWidth={3}
                                  dot={false}
                                  name="After Investment"
                                />
                              )}

                            </LineChart>

                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
  
                  
                    {snapshotViewMode === "governance" && (
                    
                      <div className="grid grid-cols-2 gap-4">
                        <div className="mt-6 ml-6 rounded-xl border border-gray-200 bg-slate-50 p-4">
                          <h3 className="text-ls font-semibold mb-3">
                          Audit Trail
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-xs text-gray-500">Created by</div>
                              <div className="font-medium">
                                {/* {selectedSnapshot.created_by?.username || "Not recorded"} */}
                                { currentUser || "System"}

                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-gray-500">Approved by</div>
                              <div className="font-medium">
                                {/* {selectedSnapshot.approved_by || "Pending approval"} */}

                                {
      
                                    currentUser || "System"
                                }
                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-gray-500">Approved at</div>
                              <div className="font-medium">
                                {selectedSnapshot.approved_at
                                  ? new Date(selectedSnapshot.approved_at).toLocaleString()
                                  : "Pending approval"}
                              </div>
                            </div>

                            <div>
                              <div className="text-xs text-gray-500">Archived at</div>
                              <div className="font-medium">
                                {selectedSnapshot.archived_at
                                  ? new Date(selectedSnapshot.archived_at).toLocaleString()
                                  : "Not archived"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-6 mr-6">
                          <h3 className="text-ls font-semibold mb-3">
                            Comments
                          </h3>

                          <textarea
                            value={governanceComment}
                            onChange={(e) =>
                              setGovernanceComment(e.target.value)
                            }
                            rows={4}
                            className="
                              w-full
                              rounded-xl
                              border
                              border-gray-300
                              p-4
                              text-sm
                              focus:outline-none
                              focus:ring-2
                              focus:ring-slate-400
                            "
                            placeholder="
                              Enter governance rationale, approval notes,
                              remediation context, or executive observations...
                            "
                          />
                        </div>
                      </div>
                      
                    )}

              
                  </div>

                  <div className="mt-6 flex justify-end gap-3 mb-6 mr-12">

                    <button
                      onClick={() => setShowSnapshotModal(false)}
                      className="px-4 py-2 rounded-xl border border-slate-300"
                    >
                      Close
                    </button>

                    {snapshotViewMode === "governance" &&  selectedSnapshot.status === "draft" && (

                      
                        <button
                          onClick={() => {
                            approveSnapshot(selectedSnapshot.id, governanceComment);
                            setShowSnapshotModal(false);
                          }}
                          className="px-4 py-2 rounded-xl bg-slate-900 text-white"
                        >
                          Approve Snapshot
                        </button>
                      )}
                  </div>
                </div>
              </div>
            )}     

          </div>
        </div>      

      )}

      {viewMode === "analysis" && (
        <ExecutiveSection
          title="Portfolio Position"
          subtitle="Baseline quantified exposure across active FAIR scenarios."
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <ExecutiveCard title="EAL" value={formatMoney(baselineEAL, settings.currency)} />
            <ExecutiveCard title="P90" value={formatMoney(baselineP90, settings.currency)} />
            <ExecutiveCard title="P95" value={formatMoney(baselineP95, settings.currency)} />
            <ExecutiveCard title="Active Scenarios" value={simulation?.allocation?.length ?? "—"} />
          </div>
        </ExecutiveSection>
      )}
      {viewMode === "analysis" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <ExecutiveSection
            title="Risk Concentration"
            subtitle="Identify concentration of expected annual loss across active scenarios."
          >
            <div className="space-y-3 mt-4">
              {contributors.map((item, index) => (
                <div key={item.scenario_id}>

                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <div className="text-sm">
                        #{index + 1} {item.incident_type} | {item.asset_name}
                      </div>

                      <div className="text-xs text-slate-500 mt-0.5">
                        EAL {formatMoney(item.expected_annual_loss, settings.currency)}
                      </div>
                    </div>

                    <span className="font-semibold text-sm">
                      {Number(item.eal_contribution_pct || 0).toFixed(1)}%
                    </span>
                  </div>

                  <div className="w-full bg-gray-100 mt-2 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        index === 0
                          ? "bg-red-500"
                          : index === 1
                          ? "bg-orange-400"
                          : "bg-blue-500"
                      }`}
                      style={{ width: `${item.eal_contribution_pct || 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-lg bg-slate-50 p-3 mt-6 text-sm text-gray-600 md:col-span-2">
              <p className="text-xs text-gray-600 ">
                <strong>{concentrationClassification}</strong>. The largest scenario contributes{" "}
                <span className="font-semibold text-gray-900">
                  {formatMoney(
                    contributors?.[0]?.expected_annual_loss || 0,
                    settings.currency
                  )}
                </span>{" "}
                representing{" "}
                <span className="font-semibold text-gray-900">
                  {top1.toFixed(1)}%
                </span>{" "}
                of total portfolio EAL
              </p>
            </div>
          </ExecutiveSection>

          <ExecutiveSection
            title="Optimal Capital Allocation"
            subtitle="Backend-modeled economic allocation across every active Scenario; baseline concentration does not determine allocation."
          >
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="border-b text-left text-xs uppercase text-slate-500">
                  <th className="p-3">Scenario</th><th className="p-3">Asset</th>
                  <th className="p-3 text-right">Optimal Allocation</th><th className="p-3 text-right">EAL Reduction</th>
                  <th className="p-3 text-right">Net Benefit</th><th className="p-3">Economic Position</th>
                </tr></thead>
                <tbody>{(optimization?.allocation || []).map((item) => (
                  <tr key={item.scenario_id} className="border-b">
                    <td className="p-3 font-medium">{item.scenario_name}</td><td className="p-3">{item.asset_name}</td>
                    <td className="p-3 text-right">{formatMoney(item.allocated_investment, settings.currency)}</td>
                    <td className="p-3 text-right">{formatMoney(item.eal_reduction, settings.currency)}</td>
                    <td className="p-3 text-right">{formatMoney(item.net_benefit, settings.currency)}</td>
                    <td className="p-3">{String(item.economic_position || "—").replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </ExecutiveSection>
        </div>
      )}

      {viewMode === "analysis" && correlation && (
        <ExecutiveSection
          title="Portfolio Correlation"
          subtitle="Identify shared dependencies that may amplify systemic portfolio risk."
        >
          {(() => {
            const score = Number(correlation.correlation_score || 0);
            const activeScenarios = Number(correlation.active_scenarios || 0);
            const topDependencies =
              correlation.systemic_dependencies?.length > 0
                ? correlation.systemic_dependencies
                : correlation.top_dependencies || [];

            const stressImpact = Number(
              correlation.correlation_stress_impact || 0
            );

            const strongestDependency = topDependencies[0];

            const strongestDependencyCount = Number(
              strongestDependency?.scenario_count || 0
            );

            const strongestDependencyShare =
              activeScenarios > 0
                ? (strongestDependencyCount / activeScenarios) * 100
                : 0;

            const exposureLabel =
              score === 0
                ? "Independent"
                : score < 30
                ? "Low"
                : score < 60
                ? "Moderate"
                : "High";


            const dependencyNarrative =
              strongestDependency
                ? `${strongestDependency.dependency} represents a ${strongestDependency.severity.toLowerCase()} systemic dependency, supporting ${strongestDependencyCount} scenario(s) and concentrating ${Number(
                    strongestDependency.eal_concentration_pct || 0
                  ).toFixed(1)}% of portfolio EAL.`
                : "No systemic dependency concentration available for the current portfolio.";


            const correlationNarrative =
              strongestDependency
                ? `${strongestDependency.dependency} represents the primary systemic concentration point, supporting ${strongestDependencyCount} active scenario(s) and concentrating ${Number(
                    strongestDependency.eal_concentration_pct || 0
                  ).toFixed(1)}% of portfolio EAL.`
                : "No material systemic dependency concentration detected across active scenarios.";

            const stressNarrative =
              stressImpact > 0
                ? `Under correlated failure conditions, portfolio P95 increases by ${stressImpact.toFixed(
                    1
                  )}%, indicating material dependency concentration risk.`
                : "No measurable tail-risk amplification detected under current correlation settings.";

            const getDependencySeverity = (share) => {
              if (share >= 70) return "Critical";
              if (share >= 40) return "High";
              if (share >= 20) return "Moderate";
              return "Low";
            };

            return (
              <>
                <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    Executive Insight
                  </div>
                  <div className="mt-1 text-sm text-blue-900">
                    {correlationNarrative} {stressNarrative}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="rounded-xl border bg-white p-4">
                    <div className="text-xs text-gray-500">Systemic Dependencies</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {topDependencies.length}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Shared concentration points
                    </div>
                  </div>

                  <div className="rounded-xl border bg-white p-4">
                    <div className="text-xs text-gray-500">Correlation Exposure</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {score}%
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Estimated systemic amplification
                    </div>
                  </div>

                  <div className="rounded-xl border bg-white p-4">
                    <div className="text-xs text-gray-500">Top Systemic Risk</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1 truncate">
                      {strongestDependency?.dependency || "—"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {strongestDependency
                        ? `${Number(
                            strongestDependency.eal_concentration_pct || 0
                          ).toFixed(1)}% of portfolio EAL`
                        : "No systemic concentration"}
                    </div>
                  </div>

                  <div className="rounded-xl border bg-white p-4">
                    <div className="text-xs text-gray-500">
                      Correlation Stress Impact
                    </div>

                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {stressImpact.toFixed(1)}%
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      P95 increase from shared dependency shocks
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setShowCorrelationDetails((prev) => !prev)}
                    className="flex w-full items-center justify-between rounded-xl border bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  >
                    <span>Advanced Correlation Details</span>
                    <span className="text-xs text-gray-500">
                      {showCorrelationDetails ? "Hide" : "Show"}
                    </span>
                  </button>

                  {showCorrelationDetails && (
                    <div className="mt-4 rounded-xl border bg-white p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">
                        Top Shared Dependencies
                      </h4>

                      <div className="space-y-3">
                        {topDependencies.length > 0 ? (
                          topDependencies.slice(0, 5).map((dep) => {
                            const count = Number(dep.scenario_count || 0);
                            const share =
                              activeScenarios > 0
                                ? (count / activeScenarios) * 100
                                : 0;

                            const severity = dep.severity || "Low";

                            return (
                              <div
                                key={dep.dependency}
                                className="rounded-lg border bg-gray-50 p-3"
                              >
                                <div className="flex items-center justify-between gap-4">
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {dep.dependency}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {Number(dep.eal_concentration_pct || 0).toFixed(1)}% of portfolio EAL
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <div className="text-sm font-bold text-gray-900">
                                      {share.toFixed(0)}%
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {severity} concentration
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3 h-2 w-full rounded-full bg-gray-200">
                                  <div
                                    className="h-2 rounded-full bg-gray-900"
                                    style={{
                                      width: `${Math.min(
                                        Number(dep.systemic_score || 0),
                                        100
                                      )}%`
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-sm text-gray-500">
                            No shared dependencies detected.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </ExecutiveSection>
      )}
            
      {viewMode === "analysis" && (

      <div className="relative">

        {loading&& (
          <div className="fixed left-[100px] right-0 bottom-0 top-[260px] z-40 flex items-start justify-center  pt-20">
            <LoadingOrbit
              message=""
              submessage=""
            />
          </div>
        )}


       <ExecutiveSection
          title="Portfolio Capital Optimization"
          subtitle="Set the maximum capital available; the validated backend optimizer may deploy less."
       >

        <EconomicBasisDisclosure
          economicHorizonYears={optimization?.methodology?.economic_horizon_years ?? optimization?.run_context?.economic_horizon_years ?? settings.economicHorizonYears}
        />

        <div className="bg-sky-900 text-white mt-4 rounded-2xl p-6 shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
            <div className="md:col-span-1">
              <p className="text-sm uppercase tracking-wide text-gray-200">
                Available Capital
              </p>

              <p className="text-4xl font-bold mt-2">
                {formatMoney(selectedInvestment, settings.currency)}
              </p>
            </div>

            <div className="md:col-span-4">

              <input
                type="range"
                min={FAIR_INVESTMENT_CONFIG.min}
                max={dynamicInvestmentMax}
                step={investmentStep}
                value={selectedInvestment}
                onChange={(e) =>
                  setSelectedInvestment(normalizeInvestment(e.target.value))
                }
                className="
                  w-full h-2 rounded-lg appearance-none cursor-pointer
                  bg-gray-200
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:h-6
                  [&::-webkit-slider-thumb]:w-6
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-cyan-400
                  [&::-webkit-slider-thumb]:shadow-lg
                  [&::-webkit-slider-thumb]:border-4
                  [&::-webkit-slider-thumb]:border-[#0B132B]
                "
              />

              <div className="flex justify-between text-xs text-slate-200 mt-2">
                <span>
                  {formatMoney(FAIR_INVESTMENT_CONFIG.min, settings.currency)}
                </span>

                <span>
                  {formatMoney(dynamicInvestmentMax, settings.currency)}
                </span>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <p className="text-sm text-slate-100" role="status">
                  {selectedInvestment !== investment ? "Changes not yet applied" : "Current assumptions applied"}
                </p>
                <button type="button" onClick={() => setExecutionInvestment(selectedInvestment)} disabled={selectedInvestment === investment || (loading && selectedInvestment === executionInvestment)} className="rounded-xl bg-cyan-400 px-4 py-2 text-sm font-semibold text-sky-950 disabled:cursor-not-allowed disabled:opacity-50">
                  {loading && selectedInvestment !== investment ? "Running..." : "Run Analysis"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-6">
          <p className="text-sm leading-relaxed text-slate-700">
            Available Capital is the maximum economic constraint, not mandatory, approved, or deployed investment. The accepted optimizer response remains visible until Run Analysis applies a pending change.
          </p>
        </div>

        {optimizationError && <p role="alert" className="mb-4 text-sm text-red-700">{optimizationError}</p>}
        {optimization && (
          <>
            {canProduce && <div className="mb-6 flex justify-end">
              {acceptanceError && <p role="alert" className="text-sm text-red-700">{acceptanceError}</p>}
              <button type="button" onClick={async () => { if (await acceptOptimization(optimization)) setContinueToAnalysis(true); }} disabled={loading || selectedInvestment !== investment} className="rounded-xl bg-cyan-700 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                Accept and Continue to Portfolio Analysis
              </button>
            </div>}
            <div className="grid grid-cols-2 md:grid-cols-4 mb-6 gap-4">
              <ExecutiveCard title="Available Capital" value={formatMoney(optimization.available_capital, settings.currency)} />
              <ExecutiveCard title="Modeled Optimal Deployment" value={formatMoney(optimization.modeled_optimal_deployment, settings.currency)} accent="success" />
              <ExecutiveCard title="Unallocated Capital" value={formatMoney(optimization.unallocated_capital, settings.currency)} />
              <ExecutiveCard title="Capital Utilization" value={formatPercent(optimization.capital_utilization_ratio, 1)} />
            </div>
            {Number(optimization.unallocated_capital) > 0 && (
              <p className="mb-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">Unallocated capital represents available capital that does not improve modeled Portfolio Net Benefit under the current economic assumptions.</p>
            )}
            {Number(optimization.available_capital) > 0 && Number(optimization.modeled_optimal_deployment) === 0 && (
              <p className="mb-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">Under the current modeled economic objective, no positive Scenario allocation improves Portfolio Net Benefit.</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 mb-6 gap-4">

              <ExecutiveCard
                title="Portfolio EAL Reduction"
                value={formatMoney(ealReduction, settings.currency)}
                accent="success"
              />

              <ExecutiveCard
                title="P90 Reduction"
                value={formatMoney(p90Reduction, settings.currency)}
                accent="success"
              />

              <ExecutiveCard
                title="P95 Reduction"
                value={formatMoney(p95Reduction, settings.currency)}
                accent="success"
              />

              <ExecutiveCard
                title="Portfolio ROI"
                value={formatROI(roi)}
                subtitle={roiInterpretation.label}
                accent={roi >= 1 ? "success" : roi > 0 ? "warning" : "danger"}
              />

              <ExecutiveCard
                title="Portfolio Net Benefit"
                value={formatMoney(netBenefit, settings.currency)}
                accent={netBenefit >= 0 ? "success" : "danger"}
              />
            </div>             

          </>
        )}

       </ExecutiveSection>
      </div>   


     )}


      {viewMode === "analysis" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
          Portfolio loss-exceedance curves are not shown for optimized results because the optimization endpoint does not return an authoritative winning-allocation curve dataset.
        </div>
      )}
    
    </div>
  );
}

function Card({ title, value }) {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <p className="text-xs text-gray-500">{title}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
